import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';

type WishlistContextType = {
  wishlist: string[]; // Product IDs
  toggleWishlist: (productId: string) => Promise<void>;
  isInWishlist: (productId: string) => boolean;
};

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
};

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [user, setUser] = useState(auth.currentUser);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u: any) => setUser(u));
    return unsubscribe;
  }, []);

  // Load wishlist
  useEffect(() => {
    if (user) {
      const docRef = doc(db, 'wishlists', user.uid);
      const unsub = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setWishlist(data.items || []);
        } else {
          setWishlist([]);
        }
      });
      return unsub;
    } else {
      // Load from localStorage
      const val = localStorage.getItem('wishlist');
      if (val) setWishlist(JSON.parse(val));
    }
  }, [user]);

  const toggleWishlist = async (productId: string) => {
    let newWishlist = [...wishlist];
    if (newWishlist.includes(productId)) {
      newWishlist = newWishlist.filter(id => id !== productId);
    } else {
      newWishlist.push(productId);
    }

    setWishlist(newWishlist);

    if (user) {
      await setDoc(doc(db, 'wishlists', user.uid), { items: newWishlist }, { merge: true });
    } else {
      localStorage.setItem('wishlist', JSON.stringify(newWishlist));
    }
  };

  const isInWishlist = (productId: string) => wishlist.includes(productId);

  return (
    <WishlistContext.Provider value={{ wishlist, toggleWishlist, isInWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
};
